import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createFantasyTeam, deleteFantasyTeam, getAllTeams, getFantasyTeams, getFantasyPlayerMatchesByTeamId, getFantasyLeaderboard } from "../../services/FantasyService";
import { useFantasyLock, formatLockCountdown, formatLockDate } from "../../hooks/useDeadlineLock";
import { AuthContext } from "../../context/AuthContext";
import { admins, currentSeason, divisionMappings } from "../../Constants";
import PrizePanel from "../FantasyPage2/PrizePanel";
import { getDivisionsBySeason } from "../../services/MatchService";
import { Division } from "../../types/types";
import { FantasyTeam } from "../../models/FantasyTeam";
import { FantasyPlayer } from "../../models/FantasyPlayer";
import { FantasyPlayerMatch } from "../../models/FantasyPlayerMatch"; // Import added
import { FantasyStartTeam } from "../../models/FantasyStartTeam";
import TeamSlotV2 from "./TeamSlotV2";
import EmptySlotV2 from "./EmptySlotV2";
import PlayerSlotV2 from "./PlayerSlotV2";
import cardStyles from './PlayerSlotV2.module.css';
import Loading from "../../components/Loading";
import { Delete } from "@mui/icons-material";
import { FantasyRole } from "../../models/FantasyRole";
import range from "lodash/range";
import { Link } from "react-router-dom";
import { parseMatchTime } from "../../utils/matchTime";
import { useTranslation } from "react-i18next";
import PriceFilterBar, { PriceRange, PRICE_STEP } from "./PriceFilterBar";

function formatDateToDanish(dateInput: string | Date): string {
    const date = parseMatchTime(dateInput);

    const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
    };

    return date.toLocaleDateString('da-DK', options).replace("kl.", "").trim();
}

const CreateFantasyPage = () => {
    const [isLoading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [selectedDivision, setSelectedDivision] = useState<Division | null>(null);
    const [currentGroup, setCurrentGroup] = useState<string | null>(null);
    const [team, setTeam] = useState<FantasyTeam | null>(null);
    const [selectedPlayers, setSelectedPlayers] = useState<FantasyPlayer[]>([]);
    const [fantasyTeams, setFantasyTeams] = useState<FantasyTeam[]>([]);
    const [allTeams, setAllTeams] = useState<FantasyStartTeam[]>([]);
    // steamId -> the real club that player starts the season on. A saved team comes back from the API
    // without `incomingTeamId` (the field is stripped on save), so without this map the "max 2 from the
    // same club" guard silently skipped every player that was already on the team when the page loaded,
    // and a manager could edit their way to 3+ from one club and then be disqualified by the sweep.
    const [clubBySteamId, setClubBySteamId] = useState<Map<string, { id: string; name: string }>>(new Map());
    const [money, setMoney] = useState<number>(1000000);
    const [ranking, setRanking] = useState<number | null>(null); // Add ranking state
    const allRoles = useMemo(() => Object.values(FantasyRole), []);
    const [availableRoles, setAvailableRoles] = useState<string[]>(() => Object.values(FantasyRole));


    // New states for matches and showing match details
    const [matches, setMatches] = useState<FantasyPlayerMatch[]>([]);
    const [showMatches, setShowMatches] = useState(false);

    // null = untouched, i.e. the whole price range of the pool.
    const [priceRange, setPriceRange] = useState<PriceRange | null>(null);

    const { t } = useTranslation('pages');

    const { user: visitor, token } = useContext(AuthContext);
    const isAdmin = visitor != null && admins.includes(visitor.steamID);

    // Signups close automatically at the season deadline. The hook keeps counting down while the page is
    // open, so a team being edited at 23:59 locks itself instead of failing on save.
    const lock = useFantasyLock();
    const isLocked = lock.locked;
    const effectiveLocked = isLocked && !isAdmin;
    // A saved team also carries its own locked flag, and that flag - not the deadline - is what puts
    // this page into the read-only points view. Admins edit teams for support after the deadline, so
    // the row flag gets the same bypass as the deadline itself.
    const teamLocked = (team?.locked ?? false) && !isAdmin;
    const lockDate = lock.lockTime ? formatLockDate(lock.lockTime, 'da-DK') : '';
    const countdown = formatLockCountdown(lock.msRemaining ?? 0, { d: 'd', h: 't', m: 'm', s: 's' });

    const onPlayerRemoveClick = useCallback((player: FantasyPlayer) => {
        const newPlayers = [...selectedPlayers];
        newPlayers.splice(newPlayers.indexOf(player), 1);
        setSelectedPlayers(newPlayers);
    }, [selectedPlayers]);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('da-DK', {
            style: 'currency',
            currency: 'DKK',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const updateAvailableRoles = useCallback(() => {
        const selectedRoles = selectedPlayers.map(p => p.role);
        const availableRoles = allRoles.filter(role => !selectedRoles.includes(role));
        setAvailableRoles(availableRoles);
    }, [selectedPlayers, allRoles]);

    // Add useEffect to watch for changes in selectedPlayers
    useEffect(() => {
        updateAvailableRoles();
    }, [selectedPlayers, updateAvailableRoles]);

    const onPlayerRoleClick = useCallback((player: FantasyPlayer, newRole: string) => {
        const roleObject = allRoles.find((r) => r === newRole) as FantasyRole;

        setSelectedPlayers((prevSelectedPlayers) =>
            prevSelectedPlayers.map((p) =>
                p.account.steamID === player.account.steamID
                    ? new FantasyPlayer(
                        p.stats,
                        p.account,
                        p.price,
                        roleObject,
                        p.playerPoints,
                        p.rolePoints,
                        p.teamPoints,
                        p.rarity,
                        p.teamId
                    )
                    : p
            )
        );
    }, [allRoles]);

    // Every draft pool we have seen this session feeds the club map, so switching group keeps the
    // clubs we already learned instead of forgetting the players picked from the previous group.
    const rememberClubs = useCallback((pools: FantasyStartTeam[]) => {
        setClubBySteamId((prev) => {
            const next = new Map(prev);
            pools.forEach((pool) => pool.players?.forEach((poolPlayer) => {
                const steamId = poolPlayer.account?.steamID;
                if (steamId) next.set(steamId, { id: pool.id, name: pool.name });
            }));
            return next;
        });
    }, []);

    const clubOf = useCallback((player: FantasyPlayer): { id: string; name: string } | null => {
        const fromPool = clubBySteamId.get(player.account.steamID);
        if (fromPool) return fromPool;
        // Fallback for a player just picked from a pool we somehow did not record.
        if (player.incomingTeamId) return { id: player.incomingTeamId, name: '' };
        return null;
    }, [clubBySteamId]);

    // The worst same-club group in the current squad, used for both the live warning and the save
    // guard so the page and the server agree on what is illegal.
    const sameClubViolation = useMemo((): { count: number; name: string } | null => {
        const counts = new Map<string, { count: number; name: string }>();
        selectedPlayers.forEach((player) => {
            const club = clubOf(player);
            if (!club) return;
            const entry = counts.get(club.id) ?? { count: 0, name: club.name };
            entry.count += 1;
            if (!entry.name && club.name) entry.name = club.name;
            counts.set(club.id, entry);
        });
        return Array.from(counts.values())
            .filter((c) => c.count > 2)
            .sort((a, b) => b.count - a.count)[0] ?? null;
    }, [selectedPlayers, clubOf]);

    const validateTeam = useCallback((): boolean => {
        // Check for 5 players
        if (selectedPlayers.length !== 5) {
            alert("Du skal vælge præcis 5 spillere.");
            return false;
        }
        
        // Check budget
        if (money < 0) {
            alert("Du har overskredet dit budget på 1.000.000.");
            return false;
        }
        
        // Check if all players have roles assigned
        if (selectedPlayers.some(player => player.role === null)) {
            alert("Du skal vælge en rolle for alle spillere.");
            return false;
        }
        
        // Check for max 2 players from same team
        if (sameClubViolation) {
            alert(sameClubViolation.name
                ? `Du har ${sameClubViolation.count} spillere fra ${sameClubViolation.name}. Du må højst have 2 spillere fra samme hold.`
                : "Du kan ikke vælge mere end 2 spillere fra samme hold.");
            return false;
        }

        return true;
    }, [selectedPlayers, money, sameClubViolation]);
    

    const onPlayerAddClick = useCallback((player: FantasyPlayer) => {
        if (!player || selectedPlayers.length === 5) {
            return;
        }

        // Resolve the club through clubOf, not player.incomingTeamId: players already on the team
        // carry no incomingTeamId, so comparing the raw field never matched them and let a fourth
        // player from the same club slip in.
        const club = clubOf(player);
        if (club) {
            const sameTeamPlayers = selectedPlayers.filter(
                (selectedPlayer) => clubOf(selectedPlayer)?.id === club.id
            );

            if (sameTeamPlayers.length >= 2) {
                alert(club.name
                    ? `Du har allerede 2 spillere fra ${club.name}. Du må højst have 2 spillere fra samme hold.`
                    : `Du kan ikke vælge mere end 2 spillere fra samme hold.`);
                return;
            }
        }

        const newPlayers = [...selectedPlayers];
        newPlayers.push(player);
        setSelectedPlayers(newPlayers);

        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [selectedPlayers, clubOf]);


    const fetchTeamRanking = useCallback(async (team: FantasyTeam) => {
        if (!team || !team.division) return;

        try {
            const leaderboard = await getFantasyLeaderboard(team.season, visitor == null ? null : visitor.steamID);
            const divisionTeams = leaderboard[team.division];
            if (divisionTeams) {
                const teamRank = divisionTeams.findIndex(t => t.account.steamID === team.account.steamID);
                setRanking(teamRank !== -1 ? teamRank + 1 : null); // Ranking starts from 1
            }
        } catch (err) {
            console.error("Error fetching team ranking:", err);
        }
    }, [visitor]);

    const fetchFantasyTeams = useCallback(async () => {
        if (!visitor) {
            return;
        }

        //if player dosnt exist create him with blank data at 200k
        try {
            const response = await getFantasyTeams(visitor.steamID);
            // Filter teams to include only those from the current season,

            //we shuld look at last played season if they dont have stats for current season.
            const currentSeasonTeams = response.filter(team => team.season === currentSeason);

            if (currentSeasonTeams.length > 0) {
                const firstTeam = currentSeasonTeams[0];
                setTeam(firstTeam);
                setSelectedPlayers(firstTeam.players);
                setSelectedDivision(divisions.find(div => div.division === firstTeam.division) || null);

                if (isLocked) {
                    await fetchTeamRanking(firstTeam); // Fetch ranking if team is locked
                }
            }
            setFantasyTeams(currentSeasonTeams);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
    }, [visitor, divisions, isLocked, fetchTeamRanking]);


    const resetEverything = useCallback(() => {
        setCurrentGroup(null);
        setSelectedPlayers([]); // Clear selected players
        setAllTeams([]);
        setMoney(1000000);
        setTeam(null); // Reset the team as well
    }, []);

    const isValidTeam = () => {
        return selectedPlayers.length === 5 && money >= 0 && !sameClubViolation;
    }

    const createNewFantasyTeam = useCallback(async (): Promise<void> => {
        if (!visitor || !token) {
            return;
        }
        
        // Validate the team before submission
        if (!validateTeam()) {
            return;
        }
    
        const fantasyTeam: FantasyTeam = {
            season: currentSeason,
            account: visitor,
            id: team?.id ?? 0,
            locked: false,
            division: selectedDivision!.division,
            players: selectedPlayers,
            points: 0,
        };
        
        setLoading(true);
        try {
            const success = await createFantasyTeam(token, fantasyTeam);
            if (success) {
                resetEverything();
                setSelectedDivision(null);
                alert("Fantasy hold oprettet!");
                window.location.reload();
            } else {
                alert("Kunne ikke oprette fantasy hold");
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            console.error(errorMessage);
            // Saves are refused once the season locks - most likely the deadline passed mid-edit.
            const apiMessage = (err as any)?.apiMessage as string | undefined;
            alert(isLocked
                ? "Tilmeldingen er lukket for denne sæson - dit hold kan ikke ændres."
                : apiMessage ?? "Der skete en fejl");
        } finally {
            setLoading(false);
        }
    }, [visitor, token, team?.id, selectedDivision, selectedPlayers, resetEverything, validateTeam, isLocked]);

    const deleteCurrentFantasyTeam = useCallback(async (): Promise<void> => {
        if (!token || !team) {
            return;
        }
        // The delete is immediate and unrecoverable, and the button is a bare icon sitting next to
        // save - so it asks first rather than wiping a scored team on a misclick.
        if (!window.confirm("Slet dette fantasy hold? Det kan ikke fortrydes.")) {
            return;
        }

        setLoading(true);
        try {
            const success = await deleteFantasyTeam(token, team);
            if (success) {
                window.location.reload();
            } else {
                alert("Kunne ikke slette fantasy holdet");
            }
        } finally {
            setLoading(false);
        }
    }, [token, team]);

    const fetchAllTeams = useCallback(async () => {
        if (!currentSeason || !selectedDivision) {
            return;
        }
        if (selectedDivision.group && !currentGroup) {
            return;
        }

        setLoading(true);
        setError(''); // Clear any previous errors
        try {
            const response = await getAllTeams(currentSeason, selectedDivision.division, currentGroup);
            setAllTeams(response);
            rememberClubs(response);
        } catch (err: any) {
            if (err.isProcessing) {
                // Show alert for 204 processing status
                alert("Data er stadig ved at blive behandlet. Prøv igen om et par minutter.");
                setError("Data behandles stadig...");
            } else {
                setError(err instanceof Error ? err.message : String(err));
            }
        } finally {
            setLoading(false);
        }
    }, [selectedDivision, currentGroup, rememberClubs]);

    // A saved squad can hold players from every group of the division, but the builder only ever has
    // one group's pool loaded. Pull all of them once per division so the same-club guard can resolve
    // every player - the pools are served straight from the API's in-memory cache.
    useEffect(() => {
        if (!selectedDivision) return;
        const groups: (string | null)[] = selectedDivision.group?.length ? selectedDivision.group : [null];
        let cancelled = false;
        (async () => {
            const pools = await Promise.all(groups.map((group) =>
                getAllTeams(currentSeason, selectedDivision.division, group).catch(() => [] as FantasyStartTeam[])
            ));
            if (!cancelled) rememberClubs(pools.flat());
        })();
        return () => { cancelled = true; };
    }, [selectedDivision, rememberClubs]);

    const loadDivisions = useCallback(async () => {
        try {
            const seasonToLoad = currentSeason - 1;
            let divisions = await getDivisionsBySeason(seasonToLoad);

            // Filter out divisions that don't exist in fantasy
            divisions = divisions.filter(div => div.toLowerCase() !== 'playoff');

            const divisionMap = new Map();
            divisions.forEach((division) => {
                const formattedDivision = divisionMappings.get(division) || division;
                // Ligaen and 1. Division run as a single group, so they go in whole. Everything
                // below them is split into A/B/C/D and gets folded together by the regex after.
                if (division === 'ligaen' || division === '1div') {
                    divisionMap.set(division, createDivision(division, formattedDivision, null));
                }

                const divisionAndGroup = division.match(/^(\d+div)([A-Z])?$/);
                if (divisionAndGroup) {
                    const [divisionPrefix, group] = divisionAndGroup.slice(1);
                    // Skip if no group letter (e.g., "1div" without A/B/C/D)
                    if (!group) return;

                    if (divisionMap.has(divisionPrefix)) {
                        const currentDivision = divisionMap.get(divisionPrefix);
                        if (currentDivision?.group) {
                            currentDivision.group.push(group);
                        } else {
                            currentDivision.group = [group];
                        }
                        divisionMap.set(divisionPrefix, currentDivision);
                    } else {
                        divisionMap.set(divisionPrefix, createDivision(divisionPrefix, formattedDivision, group));
                    }
                }
            });
            const divisionArray = Array.from(divisionMap.values());
            divisionArray.sort((a, b) => {
                // Sort by division hierarchy: 1div -> 2div -> etc.
                const getOrder = (div: string) => {
                    if (div === 'ligaen') return 0;
                    if (div === '1div') return 1;
                    if (div === '2div') return 2;
                    if (div === '3div') return 3;
                    if (div === '4div') return 4;
                    if (div === '5div') return 5;
                    return 6;
                };
                return getOrder(a.division) - getOrder(b.division);
            });
            setDivisions(divisionArray);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
    }, []);

    const createDivision = (divisionPrefix: string, divisionName: string, group: string | null) => {
        if (divisionPrefix === 'ligaen' || divisionPrefix === '1div') {
            return {
                division: divisionPrefix,
                formattedDivision: divisionName,
                group: null,
            };
        }
        const splittedDiv = divisionName.split(" ", 2);
        const div = `${splittedDiv[0]} ${splittedDiv[1]}`;
        return {
            division: divisionPrefix,
            formattedDivision: div,
            group: group ? [group] : null,
        };
    };

    useEffect(() => {
        fetchAllTeams();
    }, [fetchAllTeams]);

    useEffect(() => {
        fetchFantasyTeams();
    }, [fetchFantasyTeams]);

    useEffect(() => {
        loadDivisions();
    }, [loadDivisions]);

    useEffect(() => {
        const calculateMoney = () => {
            const totalSpent = selectedPlayers.reduce((acc, player) => acc + player.price, 0);
            setMoney(1000000 - totalSpent);
        };

        calculateMoney();
    }, [selectedPlayers]);

    useEffect(() => {
        if (currentSeason && selectedDivision) {
            // Only fetch the team if it's in the same division and not locked
            const team = fantasyTeams.find(team => team.season === currentSeason && team.division === selectedDivision.division);
            if (team && !effectiveLocked) {
                setTeam(team);
                setSelectedPlayers(team.players);
            } else {
                setTeam(null); // Ensure no team is selected if not found in the division
            }
        }
    }, [selectedDivision, fantasyTeams, effectiveLocked]);


    // ===== Price filter =====
    // The whole pool sits in a narrow band (a division is typically ~165k-255k), so the slider
    // is bounded by the actual pool instead of the 1.000.000 budget - otherwise every player
    // would bunch up in a fifth of the track.
    const priceBounds = useMemo((): PriceRange | null => {
        let lo = Infinity;
        let hi = -Infinity;
        for (const startTeam of allTeams) {
            for (const player of startTeam.players) {
                if (player.price < lo) lo = player.price;
                if (player.price > hi) hi = player.price;
            }
        }
        if (!isFinite(lo)) return null;

        const min = Math.floor(lo / PRICE_STEP) * PRICE_STEP;
        const max = Math.ceil(hi / PRICE_STEP) * PRICE_STEP;
        return { min, max: max === min ? min + PRICE_STEP : max };
    }, [allTeams]);

    // A new division is a new price band, so an old range would be meaningless there. Groups inside
    // one division share a band, so the filter survives an A/B/C/D flip - that is the whole point of
    // it: hunting the same budget across the groups until someone in it turns up.
    useEffect(() => {
        setPriceRange(null);
    }, [selectedDivision?.division]);

    const activeRange = priceRange ?? priceBounds;
    const isPriceFiltered = priceBounds !== null && activeRange !== null
        && (activeRange.min > priceBounds.min || activeRange.max < priceBounds.max);

    const visibleTeams = useMemo(() => {
        if (!isPriceFiltered || !activeRange) return allTeams;

        return allTeams
            .map((startTeam) => {
                const players = startTeam.players.filter(p => p.price >= activeRange.min && p.price <= activeRange.max);
                return players.length === startTeam.players.length
                    ? startTeam
                    : new FantasyStartTeam(startTeam.id, startTeam.name, startTeam.imageURL, startTeam.season, startTeam.division, players);
            })
            .filter(startTeam => startTeam.players.length > 0);
    }, [allTeams, activeRange, isPriceFiltered]);

    const poolCounts = useMemo(() => ({
        total: allTeams.reduce((acc, startTeam) => acc + startTeam.players.length, 0),
        shown: visibleTeams.reduce((acc, startTeam) => acc + startTeam.players.length, 0),
    }), [allTeams, visibleTeams]);

    const slotsLeft = 5 - selectedPlayers.length;

    // The most a single pick may cost while the remaining empty slots can still be filled:
    // every other slot has to be covered by at least the cheapest player still on the board.
    const affordableCeiling = useMemo((): number | null => {
        if (slotsLeft <= 0) return null;

        const chosen = new Set(selectedPlayers.map(p => p.account.steamID));
        let cheapest = Infinity;
        for (const startTeam of allTeams) {
            for (const player of startTeam.players) {
                if (!chosen.has(player.account.steamID) && player.price < cheapest) cheapest = player.price;
            }
        }
        if (!isFinite(cheapest)) return null;

        return money - cheapest * (slotsLeft - 1);
    }, [allTeams, selectedPlayers, money, slotsLeft]);

    const handleViewMatches = useCallback(async () => {
        if (team) {
            try {
                let fetchedMatches = await getFantasyPlayerMatchesByTeamId(team.id);
                fetchedMatches = fetchedMatches.sort((a, b) => parseMatchTime(b.matchTime).getTime() - parseMatchTime(a.matchTime).getTime());
                setMatches(fetchedMatches);
                setShowMatches(!showMatches);
            } catch (err) {
                setError("Failed to fetch matches.");
            }
        }
    }, [team, showMatches]); // Ensure showMatches is in the dependency array

    if (!visitor) {
        return (
            <div className="container text-center">
                <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Du er ikke logget ind, log venligst ind.</p>
            </div>
        )
    }

    if (token === null) {
        return (
            <div className="container text-center">
                <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Du er ikke logget ind, log venligst ind.</p>
            </div>
        )
    }

    if (error) {
        return (
            <div>
                <div className="container text-center">
                    <h1 style={{ color: '#e2e8f0' }}>Opret Fantasy hold</h1>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Her kan du oprette et fantasy hold for hver division. Vælg en spiller ved at trykke på spilleren</p>
                    <hr style={{ borderColor: 'rgba(255, 102, 0, 0.2)' }} />
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Der skete en fejl</p>
                </div>
            </div>
        )
    }

    return (
        <div>
            <div className="container text-center">
                <h1 style={{ color: '#e2e8f0' }}>Opret Fantasy hold</h1>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Her kan du oprette et fantasy hold for hver division. Vælg en spiller ved at trykke på spilleren</p>

                {/* Countdown to the auto-lock, so nobody is surprised by the deadline mid-edit */}
                {lock.loaded && lock.lockTime && (
                    <div
                        className="d-inline-flex align-items-center gap-2 mb-3"
                        title={`Tilmelding lukker ${lockDate}`}
                        style={{
                            background: isLocked ? 'rgba(231, 76, 60, 0.12)' : lock.urgent ? 'rgba(255, 159, 10, 0.14)' : 'rgba(255, 255, 255, 0.05)',
                            border: `1px solid ${isLocked ? 'rgba(231, 76, 60, 0.4)' : lock.urgent ? 'rgba(255, 159, 10, 0.4)' : 'rgba(255, 102, 0, 0.2)'}`,
                            color: isLocked ? '#f87171' : lock.urgent ? '#fbbf24' : 'rgba(255, 255, 255, 0.75)',
                            borderRadius: '20px',
                            padding: '6px 14px',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            fontVariantNumeric: 'tabular-nums'
                        }}
                    >
                        {isLocked ? `Tilmelding lukket ${lockDate}` : `Låser om ${countdown} (${lockDate})`}
                        {isLocked && isAdmin && <span style={{ opacity: 0.7, fontWeight: 500 }}>· admin kan stadig rette</span>}
                    </div>
                )}

                {/* What the season's winners get — same source as the Fantasy hub */}
                <div className="mx-auto text-start" style={{ maxWidth: 640 }}>
                    <PrizePanel season={currentSeason} />
                </div>

                {/* Error notification */}
                {error && (
                    <div className="alert alert-warning mx-auto mb-3" style={{
                        maxWidth: '600px',
                        backgroundColor: error.includes("behandles") ? 'rgba(255, 193, 7, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                        border: error.includes("behandles") ? '1px solid rgba(255, 193, 7, 0.3)' : '1px solid rgba(220, 53, 69, 0.3)',
                        color: error.includes("behandles") ? '#ffc107' : '#dc3545'
                    }}>
                        {error.includes("behandles") && (
                            <strong>⏳ </strong>
                        )}
                        {error}
                    </div>
                )}
                
                <div className="d-flex justify-content-center p-1">
                    <Link
                        className="btn"
                        to="/fantasy/guide"
                        style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            color: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 102, 0, 0.2)',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontWeight: '500',
                            transition: 'background-color 0.2s ease, border-color 0.2s ease',
                            textDecoration: 'none'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 102, 0, 0.1)';
                            e.currentTarget.style.borderColor = 'rgba(255, 102, 0, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                            e.currentTarget.style.borderColor = 'rgba(255, 102, 0, 0.2)';
                        }}
                    >
                        Se guide og rolle info
                    </Link>
                </div>
                {
                    !effectiveLocked && (
                        <>
                            <hr style={{ borderColor: 'rgba(255, 102, 0, 0.2)' }} />
                            <div className="row justify-content-center">
                                <div className="col-12 col-md-4">
                                    <label htmlFor="divisionDropdown" style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '8px', display: 'block' }}>Vælg division</label>
                                    <div className="dropdown mb-2">
                                        <button
                                            className="btn dropdown-toggle w-100"
                                            type="button"
                                            id="divisionDropdown"
                                            data-bs-toggle="dropdown" 
                                            aria-expanded="false"
                                            style={{
                                                background: 'rgba(255, 255, 255, 0.05)',
                                                color: 'rgba(255, 255, 255, 0.9)',
                                                border: '1px solid rgba(255, 102, 0, 0.2)',
                                                borderRadius: '6px',
                                                padding: '8px 16px',
                                                fontWeight: '500',
                                                transition: 'background-color 0.2s ease, border-color 0.2s ease'
                                            }}
                                        >
                                            {selectedDivision ? selectedDivision.formattedDivision : "Alle"}
                                        </button>
                                        <ul className="dropdown-menu w-100" aria-labelledby="divisionDropdown" style={{ background: '#1a1a1a' }}>
                                            {divisions.map((division) => (
                                                <li key={division.division}>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => {
                                                            setSelectedDivision(division);
                                                            resetEverything();  // Already resets players and other details
                                                            setTeam(null);      // Reset team so it doesn't default to the existing one
                                                        }}
                                                        style={{ 
                                                            background: '#1a1a1a', 
                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                            transition: 'background 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 102, 0, 0.2)'}
                                                        onMouseLeave={(e) => e.currentTarget.style.background = '#1a1a1a'}
                                                    >
                                                        {division.formattedDivision}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>

                                    </div>
                                </div>
                            </div>
                        </>
                    )
                }

                {ranking !== null && (
                    <p><strong>Current Ranking:</strong> {ranking}</p>
                )}
            </div>
            <hr style={{ borderColor: 'rgba(255, 102, 0, 0.2)' }} />
            <div className="d-flex justify-content-between p-2">
                <div>
                    {
                        teamLocked ? (
                            <h4 className="text-center">Full Team Points: {team?.points}</h4>
                        ) : (
                            <>
                                <h4 className="text-center">Saldo: {formatCurrency(money)}</h4>
                                {money < 0 && <p className="text-center">Du har for lav saldo. Du kan ikke lave hold med negative saldo.</p>}
                                {sameClubViolation ? (
                                    <p className="text-center mb-0" style={{ color: '#ff6b6b', fontWeight: 600 }}>
                                        ⚠️ Du har {sameClubViolation.count} spillere fra {sameClubViolation.name || 'samme hold'}.
                                        Du må højst have 2 — ellers tæller holdet ikke med på ranglisten.
                                    </p>
                                ) : (
                                    <p className="text-center mb-0" style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem' }}>
                                        Maks. 2 spillere fra samme hold.
                                    </p>
                                )}
                            </>
                        )
                    }
                </div>
                <div>
                    {
                        team !== null && !effectiveLocked && (
                            <button className="btn btn-danger fantasy-btn mx-2" onClick={deleteCurrentFantasyTeam}>
                                <Delete />
                            </button>
                        )
                    }
                    {!effectiveLocked ? (
                        <button 
                            className="btn" 
                            onClick={createNewFantasyTeam} 
                            disabled={!isValidTeam()}
                            style={{
                                background: !isValidTeam() ? 'rgba(100, 100, 100, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                                color: !isValidTeam() ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.9)',
                                border: `1px solid ${!isValidTeam() ? 'rgba(100, 100, 100, 0.3)' : 'rgba(255, 102, 0, 0.2)'}`,
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontWeight: '500',
                                transition: 'background-color 0.2s ease, border-color 0.2s ease',
                                cursor: !isValidTeam() ? 'not-allowed' : 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                if (isValidTeam()) {
                                    e.currentTarget.style.background = 'rgba(255, 102, 0, 0.1)';
                                    e.currentTarget.style.borderColor = 'rgba(255, 102, 0, 0.3)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (isValidTeam()) {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                    e.currentTarget.style.borderColor = 'rgba(255, 102, 0, 0.2)';
                                }
                            }}
                        >
                            Opret/Opdater Fantasy Team
                        </button>
                    ) : (
                        <button 
                            className="btn" 
                            disabled
                            style={{
                                background: 'rgba(100, 100, 100, 0.2)',
                                color: 'rgba(255, 255, 255, 0.3)',
                                border: '1px solid rgba(100, 100, 100, 0.3)',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontWeight: '500',
                                cursor: 'not-allowed'
                            }}
                        >
                            Team Locked
                        </button>
                    )}
                </div>
            </div>


            <div className={cardStyles.selectedPlayersRow}>
                {
                    selectedPlayers.map((player) => (
                        <div className={cardStyles.selectedPlayerCol} key={player.account.steamID}>
                            <PlayerSlotV2
                                player={player}
                                isSelected={true}
                                onPlayerRemoveClick={onPlayerRemoveClick}
                                locked={teamLocked}
                            />
                            {
                                teamLocked ? (
                                    <div className="mt-2 p-2 text-center" style={{ border: '1px solid #ccc', borderRadius: '5px' }}>
                                        <strong>Current Points: </strong>
                                        <span>{player.totalPoints}</span>
                                    </div>
                                ) : (
                                    <div className="dropdown mt-2">
                                        <button 
                                            className="btn dropdown-toggle" 
                                            type="button" 
                                            id="dropdownMenuButton" 
                                            data-bs-toggle="dropdown" 
                                            aria-expanded="false"
                                            style={{
                                                background: 'rgba(255, 255, 255, 0.05)',
                                                color: 'rgba(255, 255, 255, 0.9)',
                                                border: '1px solid rgba(255, 102, 0, 0.2)',
                                                borderRadius: '6px',
                                                padding: '6px 12px',
                                                fontWeight: '500',
                                                transition: 'background-color 0.2s ease, border-color 0.2s ease'
                                            }}
                                        >
                                            {player.role ? player.role : "Vælg en rolle"}
                                        </button>
                                        <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                            {availableRoles.map((role) => (
                                                <li key={role}>
                                                    <button className="dropdown-item" onClick={() => onPlayerRoleClick(player, role)}>
                                                        {role}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )
                            }
                        </div>
                    ))
                }

                {
                    range(5 - selectedPlayers.length).map((_, index) => (
                        <div className={cardStyles.selectedPlayerCol} key={index}>
                            <EmptySlotV2 />
                        </div>
                    ))
                }
            </div>
            {teamLocked && (
                <>
                    <hr style={{ borderColor: 'rgba(255, 102, 0, 0.2)' }} />
                    <div className="row justify-content-center">
                        <div className="col-12 text-center">
                            <h4>Detailed Breakdown</h4>
                            <hr style={{ borderColor: 'rgba(255, 102, 0, 0.2)' }} />
                        </div>
                    </div>
                    <div className="row justify-content-center">
                        {selectedPlayers.map((player) => (
                            <div className="col-12 col-md-2 mb-4" key={player.account.steamID}>
                                <div className="p-2 text-center" style={{ backgroundColor: '#333', color: '#fff', borderRadius: '5px', border: '1px solid #ccc' }}>
                                    <p style={{ margin: 0 }}><strong>Player Points:</strong> {player.playerPoints}</p>
                                    <p style={{ margin: 0 }}><strong>Role Points:</strong> {player.rolePoints}</p>
                                    <p style={{ margin: 0 }}><strong>Team Points:</strong> {player.teamPoints}</p>
                                    <p style={{ margin: 0, fontWeight: 'bold' }}><strong>Total Points:</strong> {player.playerPoints + player.rolePoints + player.teamPoints}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="row justify-content-center mt-4">
                        <div className="col-12 text-center">
                            <button 
                                className="btn" 
                                onClick={handleViewMatches}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    border: '1px solid rgba(255, 102, 0, 0.2)',
                                    borderRadius: '6px',
                                    padding: '8px 16px',
                                    fontWeight: '500',
                                    transition: 'background-color 0.2s ease, border-color 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 102, 0, 0.1)';
                                    e.currentTarget.style.borderColor = 'rgba(255, 102, 0, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                    e.currentTarget.style.borderColor = 'rgba(255, 102, 0, 0.2)';
                                }}
                            >
                                {showMatches ? "Hide Detailed Games" : "View Detailed Games"}
                            </button>
                        </div>
                    </div>
                    {showMatches && matches.length > 0 && (
                        <div className="row justify-content-center mt-4">
                            <div className="col-12 text-center">
                                <h4 style={{ color: '#e2e8f0' }}>Match Details</h4>
                                <table style={{ 
                                    width: '100%',
                                    backgroundColor: 'transparent',
                                    borderCollapse: 'collapse'
                                }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid rgba(255, 102, 0, 0.3)' }}>
                                            <th style={{ color: '#ff9933', fontWeight: '600', padding: '12px 8px', textAlign: 'left', background: 'transparent' }}>Time</th>
                                            <th style={{ color: '#ff9933', fontWeight: '600', padding: '12px 8px', textAlign: 'left', background: 'transparent' }}>Player Name</th>
                                            <th style={{ color: '#ff9933', fontWeight: '600', padding: '12px 8px', textAlign: 'left', background: 'transparent' }}>Player Points</th>
                                            <th style={{ color: '#ff9933', fontWeight: '600', padding: '12px 8px', textAlign: 'left', background: 'transparent' }}>Role Points</th>
                                            <th style={{ color: '#ff9933', fontWeight: '600', padding: '12px 8px', textAlign: 'left', background: 'transparent' }}>Team Points</th>
                                            <th style={{ color: '#ff9933', fontWeight: '600', padding: '12px 8px', textAlign: 'left', background: 'transparent' }}>Total Points</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {matches.map((match: FantasyPlayerMatch) => (
                                            <tr 
                                                key={match.matchId} 
                                                style={{ 
                                                    cursor: 'pointer',
                                                    backgroundColor: matches.indexOf(match) % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent',
                                                    transition: 'background-color 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 102, 0, 0.05)'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = matches.indexOf(match) % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'}
                                            >
                                                <td style={{ color: 'rgba(255, 255, 255, 0.9)', padding: '10px 8px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', background: 'transparent' }}>{formatDateToDanish(match.matchTime)}</td>
                                                <td style={{ color: 'rgba(255, 255, 255, 0.9)', padding: '10px 8px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', background: 'transparent' }}>{team?.players.find(p => p.account.steamID === match.steamId)?.account.playerName}</td>
                                                <td style={{ color: 'rgba(255, 255, 255, 0.9)', padding: '10px 8px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', background: 'transparent' }}>{match.playerPoints}</td>
                                                <td style={{ color: 'rgba(255, 255, 255, 0.9)', padding: '10px 8px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', background: 'transparent' }}>{match.rolePoints}</td>
                                                <td style={{ color: 'rgba(255, 255, 255, 0.9)', padding: '10px 8px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', background: 'transparent' }}>{match.teamPoints}</td>
                                                <td style={{ color: '#ff9933', fontWeight: '600', padding: '10px 8px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', background: 'transparent' }}>{match.playerPoints + match.rolePoints + match.teamPoints}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
            <hr style={{ borderColor: 'rgba(255, 102, 0, 0.2)' }} />
            {
                !teamLocked && divisions.find((division) => division.division === selectedDivision?.division)?.group && (
                    <div className="row justify-content-center text-center">
                        <div className="col-12 col-md-4">
                            Vælg gruppe
                            <div className="dropdown mb-2">
                                <button 
                                    className="btn dropdown-toggle w-100" 
                                    type="button" 
                                    id="seasonDropdown" 
                                    data-bs-toggle="dropdown" 
                                    aria-expanded="false"
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        border: '1px solid rgba(255, 102, 0, 0.2)',
                                        borderRadius: '6px',
                                        padding: '8px 16px',
                                        fontWeight: '500',
                                        transition: 'background-color 0.2s ease, border-color 0.2s ease'
                                    }}
                                >
                                    {currentGroup ? currentGroup[0] : 'Vælg gruppe'}
                                </button>
                                <ul className="dropdown-menu w-100" aria-labelledby="groupDropdown">
                                    {
                                        divisions.find((division) => division.division === selectedDivision?.division)?.group?.map((group) => (
                                            <li key={group}>
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() => setCurrentGroup(group)}
                                                >
                                                    {group}
                                                </button>
                                            </li>
                                        ))
                                    }
                                </ul>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                isLoading ? (
                    <Loading />
                ) : (
                    <>
                        {priceBounds && activeRange && (
                            <PriceFilterBar
                                bounds={priceBounds}
                                value={activeRange}
                                onChange={setPriceRange}
                                affordableCeiling={affordableCeiling}
                                slotsLeft={slotsLeft}
                                shown={poolCounts.shown}
                                total={poolCounts.total}
                            />
                        )}

                        {visibleTeams.map((team) => (
                            <TeamSlotV2
                                key={team.id}
                                team={team}
                                onPlayerAddClick={(player: FantasyPlayer) => onPlayerAddClick(player)}
                                selectedPlayers={selectedPlayers}
                            />
                        ))}

                        {priceBounds && visibleTeams.length === 0 && (
                            <div className="text-center py-5" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                <p className="mb-2">{t('fantasy.priceFilter.noMatches')}</p>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={() => setPriceRange(null)}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        border: '1px solid rgba(255, 102, 0, 0.2)',
                                        borderRadius: '6px',
                                        padding: '8px 16px',
                                        fontWeight: '500'
                                    }}
                                >
                                    {t('fantasy.priceFilter.showAll')}
                                </button>
                            </div>
                        )}
                    </>
                )
            }
        </div>
    )
}

export default CreateFantasyPage;
